import React, { useEffect, useRef, useState } from 'react'

import { cn } from '../../utils/common'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

import { TableColumn } from './TableHead'
import {
  Alignment,
  FreezePosition,
  getStickyOffsets,
  getTextContent,
  isFirstRightFrozenColumn,
  isLastLeftFrozenColumn,
} from './tableHelper'

const TruncatedCell = ({ children }: { children: React.ReactNode }) => {
  const cellRef = useRef<HTMLDivElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  useEffect(() => {
    const checkOverflow = () => {
      const element = cellRef.current
      if (element) {
        const isOverflow = element.scrollWidth > element.clientWidth
        setIsOverflowing(isOverflow)
      }
    }

    checkOverflow()

    window.addEventListener('resize', checkOverflow)
    return () => window.removeEventListener('resize', checkOverflow)
  }, [children])

  const textContent = getTextContent(children)

  return (
    <Tooltip delayDuration={300}>
      <TooltipTrigger asChild>
        <div ref={cellRef} className="truncate">
          {children}
        </div>
      </TooltipTrigger>
      {isOverflowing && textContent && (
        <TooltipContent side="top" className="max-w-sm">
          <div className="break-words">{textContent}</div>
        </TooltipContent>
      )}
    </Tooltip>
  )
}

const getCellClickHandler = <T extends object>(
  col: TableColumn<T>,
  row: T
): ((e: React.MouseEvent<HTMLTableCellElement>) => void) | undefined => {
  if (col.id === 'actions' || col.disableRowClick) {
    return (e: React.MouseEvent<HTMLTableCellElement>) => e.stopPropagation()
  }
  if (col.onCellClick) {
    return (e: React.MouseEvent<HTMLTableCellElement>) => {
      e.stopPropagation()
      col.onCellClick?.(row, e)
    }
  }
  return undefined
}

const getAlignmentClass = (align: Alignment): string => {
  if (align === Alignment.RIGHT) {
    return 'text-right'
  }
  if (align === Alignment.CENTER) {
    return 'text-center'
  }
  return ''
}

export type TableBodyProps<T extends object = Record<string, unknown>> = {
  columns: TableColumn<T>[]
  data: T[]
  className?: string
  onRowClick?: (row: T) => void
}

export default function TableBody<T extends object = Record<string, unknown>>({
  columns,
  data,
  className,
  onRowClick,
}: Readonly<TableBodyProps<T>>) {
  const { leftOffsets, rightOffsets } = getStickyOffsets(columns)

  return (
    <tbody className={className}>
      {data?.map((row, rowIndex) => {
        let rowKey: string | number
        if ('id' in row) {
          rowKey = String(row.id)
        } else if ('_id' in row) {
          rowKey = String((row as { _id: unknown })._id)
        } else {
          rowKey = rowIndex
        }

        return (
          <tr
            key={rowKey}
            className={cn(
              'border-b border-[--Colors-Gray-light-300] transition-colors group hover:bg-gray-50',
              onRowClick && 'cursor-pointer'
            )}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
          >
            {columns.map((col, i) => {
              const align = col.align ?? Alignment.LEFT
              const isSticky = col.isFrozen
              const side = col.freezePosition ?? FreezePosition.LEFT
              const isLastLeftFrozen = isLastLeftFrozenColumn(columns, i)
              const isFirstRightFrozen = isFirstRightFrozenColumn(columns, i)

              let stickyStyle: React.CSSProperties = {}
              if (side === FreezePosition.LEFT) {
                stickyStyle = { left: leftOffsets[i] }
              } else if (side === FreezePosition.RIGHT) {
                stickyStyle = { right: rightOffsets[i] }
              }

              const cellValue =
                typeof col.id === 'string' && !(col.id in row)
                  ? undefined
                  : (row[col.id as keyof T] as unknown)

              const renderedContent = col.renderCell
                ? col.renderCell(
                    (cellValue ?? undefined) as T[keyof T] | undefined,
                    row
                  )
                : ((cellValue ?? '-') as React.ReactNode)

              const cellClassName =
                typeof col.cellClassName === 'function'
                  ? col.cellClassName(row)
                  : col.cellClassName

              const alignmentClass = getAlignmentClass(align)

              return (
                <td
                  key={String(col.id)}
                  className={cn(
                    'px-5 py-3 m-0 text-gray-900 border-t border-x-0 border-b-0 border-[--Colors-Gray-light-300] border-solid',
                    alignmentClass,
                    i !== 0 && 'border-[--Colors-Gray-light-300]',
                    isSticky && 'sticky bg-white group-hover:bg-gray-50 z-[3]',
                    cellClassName
                  )}
                  style={{
                    ...(col.width
                      ? { minWidth: col.width, maxWidth: col.width }
                      : {}),
                    ...stickyStyle,
                    ...(isLastLeftFrozen && {
                      boxShadow: 'inset -1px 0 0 #e5e7eb',
                    }),
                    ...(isFirstRightFrozen && {
                      boxShadow: 'inset 1px 0 0 #e5e7eb',
                    }),
                  }}
                  onClick={getCellClickHandler(col, row)}
                >
                  {col.id === 'actions' || col.disableTruncation ? (
                    renderedContent
                  ) : (
                    <TruncatedCell>{renderedContent}</TruncatedCell>
                  )}
                </td>
              )
            })}
          </tr>
        )
      })}
    </tbody>
  )
}
